import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import BlogLayout from '../../components/blog/BlogLayout';
import BlogCard from '../../components/blog/BlogCard';
import BlogSearch from '../../components/blog/BlogSearch';
import useBlogPosts from '../../hooks/useBlogPosts';

const POSTS_PER_PAGE = 9;

export default function BlogListPage() {
  const { posts, meta, loading, error } = useBlogPosts();
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);

  const searchQuery = searchParams.get('q') || '';
  const selectedCategory = searchParams.get('category') || null;
  const selectedTags = searchParams.get('tag')?.split(',').filter(Boolean) || [];

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          next.delete(key);
        } else if (Array.isArray(value)) {
          next.set(key, value.join(','));
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
    setVisibleCount(POSTS_PER_PAGE);
  }, [setSearchParams]);

  const handleSearchChange = useCallback((q) => updateParams({ q }), [updateParams]);
  const handleCategoryChange = useCallback((cat) => updateParams({ category: cat }), [updateParams]);
  const handleTagToggle = useCallback((tag) => {
    const next = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    updateParams({ tag: next });
  }, [selectedTags, updateParams]);

  const filtered = useMemo(() => {
    let result = posts;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }

    if (selectedTags.length > 0) {
      result = result.filter((p) =>
        selectedTags.every((t) => p.tags.includes(t))
      );
    }

    return result;
  }, [posts, searchQuery, selectedCategory, selectedTags]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <BlogLayout>
      {/* Hero */}
      <section className="bg-gray-50 dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <h1 className="text-4xl font-black tracking-tight text-gray-900 dark:text-white mb-2">
            Blog
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Insights on POPIA compliance, document management, and data protection.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Search & Filter */}
        <BlogSearch
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          selectedTags={selectedTags}
          onTagToggle={handleTagToggle}
          categories={meta.categories}
          tags={meta.tags}
        />

        {/* Content */}
        <div className="mt-8">
          {loading && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Loading posts...
            </div>
          )}

          {error && (
            <div className="text-center py-12 text-red-500">
              {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No posts found. Try a different search or filter.
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>

              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => setVisibleCount((c) => c + POSTS_PER_PAGE)}
                    className="px-6 py-2.5 rounded-lg border border-gray-200 dark:border-dark-border text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </BlogLayout>
  );
}
